import React, { useEffect } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { memberService } from '../../services/member.service';
import type { RegisteredMember, MemberFormData } from '../../types/member';
import { usePermissions } from '../../hooks/usePermissions';
import { FaLock } from 'react-icons/fa';

interface MemberFormProps {
  editingMember?: RegisteredMember | null;
  onSuccess?: () => void;
  readOnly?: boolean;
}

export const MemberForm: React.FC<MemberFormProps> = ({ editingMember, onSuccess, readOnly = false }) => {
  const { canWrite } = usePermissions();
  const isReadOnly = readOnly || !canWrite('members');
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset, setValue } = useForm<MemberFormData>();

  useEffect(() => {
    if (editingMember) {
      reset(editingMember);
    } else {
      reset();
      // 신규 등록 시 국가번호 기본값 설정
      setValue('countryCode', '+36');
    }
  }, [editingMember, reset, setValue]);

  const onSubmit: SubmitHandler<MemberFormData> = async (data) => {
    try {
      if (editingMember) {
        await memberService.updateRegisteredMember(editingMember.id, data);
        alert('성도 정보가 성공적으로 수정되었습니다.');
      } else {
        // addRegisteredMember가 맞습니다.
        await memberService.addRegisteredMember(data);
        alert('새로운 등록교인이 추가되었습니다.');
      }
      onSuccess?.();
    } catch (error) { /* ... */ }
  };

  return (
    <div className="space-y-8">
      {/* 읽기 전용 모드 알림 */}
      {isReadOnly && (
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
          <div className="flex items-center space-x-2">
            <FaLock className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
            <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
              읽기 전용 모드입니다. 정보를 수정할 권한이 없습니다.
            </span>
          </div>
        </div>
      )}
      
      <form onSubmit={isReadOnly ? undefined : handleSubmit(onSubmit)} className="space-y-8">
      <div className="p-6 border border-gray-300 rounded-md shadow-md space-y-6"> {/* New inner box */}
        <fieldset className="space-y-6">
          <legend className="text-xl font-semibold text-gray-600 border-b pb-2 mb-4 w-full">1. 개인신상</legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-1">
            <label htmlFor="name" className="block text-base font-normal text-gray-600 dark:text-gray-300">이름</label>
            <input
              id="name"
              {...register('name', { required: '이름은 필수 항목입니다.' })}
              lang="ko"
              disabled={isReadOnly}
              className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>}
          </div>
          <div className="md:col-span-1">
            <label htmlFor="dateOfBirth" className="block text-base font-normal text-gray-600 dark:text-gray-300">생년월일</label>
            <input
              type="text"
              id="dateOfBirth"
              {...register('dateOfBirth', { required: '생년월일은 필수 항목입니다.' })}
              placeholder="YYYYMMDD (예: 19760715)"
              maxLength={10}
              lang="ko"
              inputMode="numeric"
              disabled={isReadOnly}
              className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
              onChange={isReadOnly ? undefined : (e) => {
                let value = e.target.value.replace(/\D/g, '');
                if (value.length >= 8) {
                  value = value.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
                }
                e.target.value = value;
              }}
            />
          </div>
          <div>
            <label htmlFor="gender" className="block text-base font-normal text-gray-600 dark:text-gray-300">성별</label>
            <select id="gender" {...register('gender')} disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm text-gray-600 font-normal py-2 px-3 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}>
              <option value="male">남성</option>
              <option value="female">여성</option>
            </select>
          </div>
          <div className="col-span-2">
            <label htmlFor="phoneNumber" className="block text-base font-normal text-gray-600 dark:text-gray-300">연락처</label>
            <div className="flex">
              <select
                id="countryCode"
                {...register('countryCode', { required: '국가번호를 선택하세요.' })}
                defaultValue="+36"
                disabled={isReadOnly}
                className={`flex-shrink-0 border border-gray-300 rounded-l-md shadow-sm py-2 px-3 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 bg-white ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
              >
                <option value="+36">헝가리 (+36)</option>
                <option value="+82">한국 (+82)</option>
                <option value="+1">미국 (+1)</option>
                <option value="+44">영국 (+44)</option>
                <option value="+81">일본 (+81)</option>
                <option value="+86">중국 (+86)</option>
                <option value="+49">독일 (+49)</option>
                <option value="+33">프랑스 (+33)</option>
              </select>
              <input
                id="phoneNumber"
                {...register('phoneNumber', { required: '연락처를 입력하세요.' })}
                placeholder="전화번호 숫자만 입력"
                lang="ko"
                inputMode="tel"
                disabled={isReadOnly}
                className={`flex-1 border border-l-0 border-gray-300 rounded-r-md shadow-sm py-2 px-3 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
              />
            </div>
             {errors.phoneNumber && <p className="mt-1 text-sm text-red-600">{errors.phoneNumber.message}</p>}
          </div>
          <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="email" className="block text-base font-normal text-gray-600 dark:text-gray-300">이메일</label>
              <input type="email" id="email" {...register('email')} lang="ko" inputMode="email" disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`} />
            </div>
            <div>
              <label htmlFor="registrationDate" className="block text-base font-normal text-gray-600 dark:text-gray-300">등록일자</label>
              <input
                type="text"
                id="registrationDate"
                {...register('registrationDate')}
                placeholder="YYYYMMDD (예: 20240115)"
                maxLength={10}
                lang="ko"
                inputMode="numeric"
                disabled={isReadOnly}
                className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}
                onChange={isReadOnly ? undefined : (e) => {
                  let value = e.target.value.replace(/\D/g, '');
                  if (value.length >= 8) {
                    value = value.replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3');
                  }
                  e.target.value = value;
                }}
              />
            </div>
          </div>
        </div>
      </fieldset>
      
      <fieldset className="space-y-6">
        <legend className="text-xl font-semibold text-gray-600 border-b pb-2 mb-4 w-full">2. 개인신앙</legend>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="position" className="block text-base font-normal text-gray-600 dark:text-gray-300">직분</label>
            <select id="position" {...register('position')} disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm text-gray-600 font-normal py-2 px-3 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}>
              <option value="member">성도</option>
              <option value="youth">청년</option>
              <option value="middle_high_school">중고등부</option>
              <option value="sunday_school">교회학교</option>
              <option value="deacon">서리집사</option>
              <option value="kwonsa">권사</option>
              <option value="ordained_deacon">안수집사</option>
              <option value="elder">장로</option>
              <option value="evangelist">전도사</option>
              <option value="pastor">목사</option>
            </select>
          </div>
          <div>
            <label htmlFor="memberClass" className="block text-base font-normal text-gray-600 dark:text-gray-300">성도 구분</label>
            <select id="memberClass" {...register('memberClass')} disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm text-gray-600 font-normal py-2 px-3 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}>
              <option value="A">A</option><option value="B">B</option><option value="R">R</option>
            </select>
          </div>
          <div>
            <label htmlFor="baptism" className="block text-base font-normal text-gray-600 dark:text-gray-300">침례</label>
            <select id="baptism" {...register('baptism')} disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm text-gray-600 font-normal py-2 px-3 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}>
              <option value="baptism">침례</option><option value="confirmation">세례</option><option value="infant_baptism">유아세례</option><option value="none">받지 않음</option>
            </select>
          </div>
          <div>
            <label htmlFor="previousChurch" className="block text-base font-normal text-gray-600 dark:text-gray-300">이전출석교회</label>
            <input id="previousChurch" {...register('previousChurch')} lang="ko" inputMode="text" disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`} />
          </div>
          {/* --- 구역 활동: 드롭다운으로 변경 --- */}
          <div>
            <label htmlFor="cellGroup" className="block text-base font-normal text-gray-600 dark:text-gray-300">구역 활동</label>
            <select id="cellGroup" {...register('cellGroup')} disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm text-gray-600 font-normal py-2 px-3 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}>
              <option value="">선택...</option>
              <option value="youth1">청년1구역</option>
              <option value="youth2">청년2구역</option>
              <option value="youth3">청년3구역</option>
              <option value="women1">여성1구역</option>
              <option value="women2">여성2구역</option>
              <option value="men">남성구역</option>
            </select>
          </div>
          {/* --- 사역팀: 드롭다운으로 변경 --- */}
          <div>
            <label htmlFor="ministryTeam" className="block text-base font-normal text-gray-600 dark:text-gray-300">사역팀</label>
            <select id="ministryTeam" {...register('ministryTeam')} disabled={isReadOnly} className={`mt-1 block w-full border border-gray-300 rounded-md shadow-sm text-gray-600 font-normal py-2 px-3 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-200 ${isReadOnly ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : ''}`}>
              <option value="">선택...</option>
              <option value="praise">찬양팀</option>
              <option value="service">봉사팀</option>
              <option value="newcomer">새가족팀</option>
              <option value="education">교육팀</option>
              <option value="manna">만나팀</option>
              <option value="mission">선교팀</option>
              <option value="media">미디어팀</option>
              <option value="etc">기타</option>
            </select>
          </div>
        </div>
      </fieldset>
      </div> {/* End of new inner box */}
      {!isReadOnly && (
        <div className="flex justify-end pt-4">
          <button type="submit" disabled={isSubmitting} className="px-4 py-1.5 bg-btn-green-bg hover:bg-btn-green-hover text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition duration-150 ease-in-out disabled:opacity-50">
            {isSubmitting ? '저장 중...' : (editingMember ? '정보 수정' : '신규 등록')}
          </button>
        </div>
      )}
      </form>
    </div>
  );
};
